import L from 'leaflet';
import Vue from 'vue';
import managePlusIcons from '../assets/img/manage-plus-icons/icons.json';

const BASE_ICON_CACHE = new Map();
const FONT_FAMILY = 'Roboto, sans-serif';

function filterAsciiValue(value) {
  return String(value || '').replace(/\uFFFD/g, '')
    .replace(/[\u{0080}-\u{10FFFF}]/gu, '');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getIconMetrics(type) {
  const padding = 5;
  const metricsByType = {
    icon: {
      size: 30,
      iconX: 20,
      iconY: 20,
    },
    circle: {
      size: 35,
      iconX: 20,
      iconY: 20,
    },
    small_circle: {
      size: 25,
      iconX: 15,
      iconY: 15,
    },
    label: {
      size: 40,
      iconX: 25,
      iconY: 40,
    },
    cluster: {
      size: 50,
      iconX: 27.5,
      iconY: 27.5,
    },
    action: {
      size: 45,
      iconX: 25,
      iconY: 42.5,
    },
    default: {
      size: 35,
      iconX: 20,
      iconY: 20,
    },
  };
  const metrics = metricsByType[type] || metricsByType.default;
  return {
    ...metrics,
    padding,
    totalSize: metrics.size + (padding * 2),
  };
}

function getClusterColor(label) {
  const labelValue = parseInt(label, 10);
  if (labelValue < 10) {
    return '#B5E28C';
  }
  if (labelValue < 99) {
    return '#F1D357';
  }
  return '#FD9C73';
}

function resolveIconColors(icon, isSelected = false, isDeleted = false, isObstructed = false, label = '') {
  let { color, text_color: textColor } = icon;

  if (icon.type === 'cluster') {
    color = getClusterColor(label);
  } else if (isSelected) {
    color = icon.color_selected || color;
    textColor = icon.text_color_selected || textColor;
  } else if (isObstructed) {
    color = 'red';
    textColor = 'white';
  }

  if (isDeleted) {
    color = 'grey';
  }

  return {
    color,
    textColor,
  };
}

function shouldRenderLabelOverlay(type, label) {
  return label !== '' && ['circle', 'label', 'action', 'cluster'].includes(type);
}

function getLabelFontSize(type, size, label) {
  switch (type) {
    case 'circle':
      if (label.length > 7) {
        return size / 8 + 1;
      }
      if (label.length > 6) {
        return size / 6 + 1;
      }
      if (label.length > 5) {
        return size / 6 + 2;
      }
      if (label.length > 3) {
        return size / 6 + 3;
      }
      return size / 4 + 2;
    case 'label':
      if (label.length > 7) {
        return size / 6;
      }
      if (label.length === 7) {
        return size / 6 + 1;
      }
      if (label.length === 6) {
        return size / 6 + 2;
      }
      return size / 4;
    case 'action':
      if (label.length > 7) {
        return size / 8 + 1;
      }
      if (label.length > 5) {
        return size / 7 + 2;
      }
      return size / 5;
    case 'cluster':
      return size / 5 + 1;
    default:
      return size / 4;
  }
}

function getLabelOverlayStyle(type, size, textColor, label) {
  const fontSize = getLabelFontSize(type, size, label);
  const common = [
    'position:absolute',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    `font-family:${FONT_FAMILY}`,
    `font-size:${fontSize}px`,
    `color:${textColor}`,
    'line-height:1',
    'text-align:center',
    'white-space:nowrap',
    'pointer-events:none',
  ];

  if (type === 'label') {
    common.push('left:5px', 'top:9px', `width:${size}px`, `height:${Math.round(size * 0.48)}px`);
  } else if (type === 'action') {
    common.push('left:5px', 'top:8px', `width:${size}px`, `height:${Math.round(size * 0.62)}px`, 'color:black');
  } else {
    common.push('left:5px', 'top:5px', `width:${size}px`, `height:${size}px`);
  }

  return common.join(';');
}

function getCountBadgeHtml(type, count) {
  if (count === 0) {
    return '';
  }

  let topAndRight = -4;
  if (type === 'circle') {
    topAndRight = 1;
  }
  if (type === 'small_circle') {
    topAndRight = 3;
  }
  if (type === 'label') {
    topAndRight = -1;
  }
  return `<span style="position:absolute;top:${topAndRight}px;right:${topAndRight}px;width:16px;height:16px;border-radius:50%;`
    + 'background:red;color:#fff;display:flex;align-items:center;justify-content:center;'
    + `font-family:${FONT_FAMILY};font-size:10px;line-height:1;pointer-events:none;">${escapeHtml(count)}</span>`;
}

Vue.mixin({
  methods: {
    getMapObjectIconMetrics(type) {
      return getIconMetrics(type);
    },
    getActionIcon(count) {
      const circle = 8;
      const padding = 5;
      const size = 10;
      const iconX = (size + (padding * 2)) / 2;
      const iconY = size;

      let svg = `<svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="${size + padding * 2}"`
        + ` height="${size + padding * 2}" viewBox="-${padding} -${padding} ${size + padding * 2} ${size + padding * 2}">`;

      if (count > 0) {
        svg += `<circle cx="${size - circle / 2}" cy="${circle / 2}" r="${circle}" fill="red"></circle>`;
        svg += `<text text-anchor="middle" x="${size - circle / 2}" y="${circle / 2 + 3}"`;
        svg += ` font-family="${FONT_FAMILY}" font-size="11px" fill="#fff">${count}</text>`;
      }

      svg += '</svg>';

      const iconUrl = `data:image/svg+xml;base64,${btoa(svg)}`;

      return L.icon({ iconUrl, iconAnchor: [iconX, iconY] });
    },
    getIconByMapObjectType(icon, count, label, isSelected = false, is_deleted = false, is_obstructed = false) {
      const normalizedIcon = icon || {
        color: 'dimgray',
        text_color: 'white',
        type: 'circle',
      };
      const metrics = getIconMetrics(normalizedIcon.type);
      const filteredLabel = filterAsciiValue(label);
      const html = this.createMapObjectIconHtml(
        normalizedIcon,
        metrics,
        count,
        filteredLabel,
        isSelected,
        is_deleted,
        is_obstructed,
      );

      return L.divIcon({
        className: 'fade-in map-object-div-icon',
        html,
        iconSize: [metrics.totalSize, metrics.totalSize],
        iconAnchor: [metrics.iconX, metrics.iconY],
      });
    },
    createMapObjectIconHtml(icon, metrics, count, label, isSelected = false,
      isDeleted = false, isObstructed = false) {
      const iconUrl = this.getBaseMapObjectIconUrl(
        icon,
        metrics.size,
        label,
        isSelected,
        isDeleted,
        isObstructed,
      );
      const { textColor } = resolveIconColors(icon, isSelected, isDeleted, isObstructed, label);
      const { totalSize } = metrics;
      const labelHtml = shouldRenderLabelOverlay(icon.type, label)
        ? `<span style="${getLabelOverlayStyle(icon.type, metrics.size, textColor, label)}">${escapeHtml(label)}</span>`
        : '';

      return `<div style="position:relative;width:${totalSize}px;height:${totalSize}px;">`
        + `<img src="${iconUrl}" alt="" draggable="false" `
        + `style="display:block;width:${totalSize}px;height:${totalSize}px;pointer-events:none;" />`
        + `${labelHtml}${getCountBadgeHtml(icon.type, count)}</div>`;
    },
    getBaseMapObjectIconUrl(icon, size, label, isSelected = false,
      isDeleted = false, isObstructed = false) {
      const { color, textColor } = resolveIconColors(
        icon,
        isSelected,
        isDeleted,
        isObstructed,
        label,
      );
      const cacheKey = JSON.stringify({
        type: icon.type,
        frame: icon.frame,
        icon: icon.icon,
        size,
        color,
        textColor,
      });

      if (!BASE_ICON_CACHE.has(cacheKey)) {
        BASE_ICON_CACHE.set(cacheKey, `data:image/svg+xml;base64,${btoa(this.createBaseSvg(icon, size, color, textColor))}`);
      }

      return BASE_ICON_CACHE.get(cacheKey);
    },
    createBaseSvg(icon, size, color, textColor) {
      const originalSize = 40;
      const radius = 5;
      const padding = 5;
      const scale = parseFloat((size / originalSize).toFixed(2));

      let svg = `<svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="${size + padding * 2}"`
        + ` height="${size + padding * 2}" viewBox="-${padding} -${padding} ${size + padding * 2} ${size + padding * 2}">`;

      if (icon.type === 'circle') {
        svg += `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${color}"/>`
          + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#fff"/>`
          + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}" fill="${color}"/>`;
      } else if (icon.type === 'small_circle') {
        svg += `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 4}" fill="${color}"/>`;
        svg += `<circle stroke-width="1" stroke="${textColor}" cx="${size / 2}" cy="${size / 2}" r="${size / 5.5}" fill="none"/>`;
      } else if (icon.type === 'action') {
        svg += `<rect stroke="${color}" transform="rotate(45 22.00000000000001,29.437499999999996) "`
          + ' id="svg_12" height="13.300008" width="13.501524" y="22.787495" x="15.249238"'
          + ` stroke-opacity="null" stroke-width="3" fill="${color}"/>`
          + `<ellipse ry="15" rx="15" id="svg_1" cy="17.75" cx="22" stroke-width="3" stroke="${color}" fill="#ffffff"/>`;
      } else if (icon.type === 'label') {
        svg += `<polygon fill="${color}" stroke="${color}" stroke-miterlimit="10"`
          + ` transform="scale(${size / originalSize}, ${size / originalSize})"`
          + ' points="20,35 17.3,29.8 14.7,24.7 20,24.7 25.3,24.7 22.6,29.8 "/>'
          + `<path fill="#ffffff" stroke="${color}" stroke-width="2" stroke-miterlimit="10"`
          + ` transform="scale(${size / originalSize}, ${size / originalSize})"`
          + ' d="M29.8,23.6H10.2c-4.3,0-7.7-3.5-7.7-7.7v-3.2 C2.5,8.5,6,5,10.2,5h19.5'
          + 'c4.3,0,7.7,3.5,7.7,7.7v3.2C37.5,20.1,34,23.6,29.8,23.6z"/>';
      } else if (icon.type === 'cluster') {
        svg += '<defs><radialGradient id="cluster" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">'
          + `<stop offset="50%" style="stop-color:${color};stop-opacity:0.9" />`
          + '<stop offset="100%" style="stop-color:rgb(255,255,255);stop-opacity:0" />'
          + '</radialGradient></defs>'
          + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="url(#cluster)"/>`;
      } else if (icon.type === 'icon') {
        if (icon.frame === 'square_rounded') {
          svg += `<rect fill="${color}" stroke="#ffffff" rx="${radius}" ry="${radius}" width="${size}" height="${size}"/>`;
        } else if (icon.frame === 'square') {
          svg += `<rect fill="${color}" stroke="#ffffff" width="${size}" height="${size}"/>`;
        } else if (icon.frame === 'circle') {
          svg += `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" stroke="#ffffff" fill="${color}"/>`;
        } else if (icon.frame === 'circle_outline') {
          svg += `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" stroke="${color}" fill="#ffffff" stroke-width="3" />`;
        } else if (icon.frame === 'triangle') {
          svg += `<path fill="#FFFFFF" stroke="${color}" transform="scale(${scale}, ${scale})" stroke-width="3"`
            + ' stroke-miterlimit="10" d="M37.5,33.5L37.5,33.5V34c0,0.2,0,0.4,0,0.4'
            + 'c0,0.2,0,0.2-0.2,0.4c0,0.2-0.2,0.2-0.4,0.4h-0.2h-0.2c-0.2,0-0.4,0.2-0.6,0'
            + 'H3.9H3.5L3.1,35c-0.2,0-0.2-0.2-0.4-0.2c0-0.2-0.2-0.2-0.2-0.4c0,0,0-0.2,0-0.4'
            + 'c0-0.2,0.2-0.2,0.2-0.6L18.8,5.5V5.3L19,5.1l0.2-0.2l0.4-0.2c0.2,0,0.2,0,0.4,0'
            + 's0.2,0.2,0.4,0.2l0.2,0.2c0.2,0,0.2,0.2,0.4,0.4L37.5,33.5z"/>';
        }

        const iconSvg = managePlusIcons[icon.icon];
        if (iconSvg) {
          iconSvg.split('/>').forEach((element) => {
            if (element.indexOf('fill') !== -1) {
              svg += `${element} transform='scale(${scale}, ${scale})'/>`;
            } else if (element !== '') {
              svg += `${element} fill="${textColor}" transform="scale(${scale}, ${scale})"/>`;
            }
          });
        }
      } else {
        svg += `<rect fill="#ffffff" stroke="${color}" rx="${radius}" ry="${radius}" width="${size}" height="${size}"/>`;
      }

      svg += '</svg>';
      return svg;
    },
  },
});
